import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, Subscription } from 'rxjs';
import { ORDERS } from 'src/app/core/constants/storage';
import { ArticleBatchDto } from 'src/app/shared/dto/article-batch.dto';
import { ArticleDto } from 'src/app/shared/dto/article.dto';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';
import { ProductionOrderDto } from 'src/app/shared/dto/production-order.dto';
import { ProductionPauseDto } from 'src/app/shared/dto/production-pause.dto';
import { ProductionTimeDto } from 'src/app/shared/dto/production-time.dto';
import { RawMaterialDto } from 'src/app/shared/dto/rawMaterial.dto';
import { SetEndDateParams } from 'src/app/shared/dto/set-end-date-params.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { ProductionOrderService } from './production-order.service';
import { MachineMeasurementsValueDto } from 'src/app/shared/dto/machine-measurements-value.dto';
import { LocalStorageService } from 'src/app/core/services/storage.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, RoomNames, formatClientRoomProcessId } from 'src/app/shared/sockets/enums/rooms';
import { SocketService } from 'src/app/shared/services/socket.service';
import { RawMaterialToUseDto } from 'src/app/shared/dto/raw-material-to-use.dto';
import { StopActionsEnum } from 'src/app/shared/enums/stop-actions.enum';

@Injectable({
    providedIn: 'root',
})
export class ProductionOrderProcessesService extends BaseService {

    public orderProcess$: Observable<ProductionOrderProcessDto>;

    private readonly orderProcessSubject = new BehaviorSubject<ProductionOrderProcessDto>(new ProductionOrderProcessDto());
    private messageSubscription: Subscription | undefined;

    private readonly orderService = inject(ProductionOrderService);
    private readonly socketService = inject(SocketService);
    private readonly localStorageService = inject(LocalStorageService);

    constructor(
        httpClient: HttpClient
    ) {
        super(httpClient, 'production-order-processes');
        this.orderProcess$ = this.orderProcessSubject.asObservable();
    }

    async setupWebSocket(id: number): Promise<void> {
        try {
            const productionOrderProcess = await this.getById(id) as ProductionOrderProcessDto;
            this.orderProcessSubject.next(productionOrderProcess);

            this.socketService.conectSocketRoom(formatClientRoomProcessId(Client.DEFAULT_CLIENT, RoomNames.PRODUCTION_ORDER_PROCESSES_ROOM, id));
            this.messageSubscription = this.socketService.getNotifications(Messages.PRODUCTION_ORDER_PROCESSES_CHANGED)
                .subscribe(async (productionOrderProcess) => {
                    this.orderProcessSubject.next(productionOrderProcess);
                });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket del Proceso de Producción con id ${id}. Error: ${e.message}`);
        }
    }

    /**
     * Refresca el proceso de orden desde el backend y emite el nuevo estado a todos los suscriptores.
     */
    async refreshOrderProcess(id: number): Promise<ProductionOrderProcessDto> {
        const productionOrderProcess = await this.getById(id) as ProductionOrderProcessDto;
        this.orderProcessSubject.next(productionOrderProcess);
        return productionOrderProcess;
    }

    disconnectWebSocket(id: number): void {
        if(this.messageSubscription){
            this.messageSubscription.unsubscribe();
        }
        this.socketService.disconnectSocketRoom(formatClientRoomProcessId(Client.DEFAULT_CLIENT, RoomNames.PRODUCTION_ORDER_PROCESSES_ROOM, id));
    }

    async setProductionProcessAsStarted(orderId: number, orderProcessId: number, userLoggedId: number): Promise<ProductionOrderProcessDto> {
        try {
            const request = this.httpClient.post<ProductionOrderProcessDto>(`${this.url}/setAsStarted`, {
                orderProcessId: orderProcessId,
                startedById: userLoggedId
            });

            const updatedProcess: ProductionOrderProcessDto = await lastValueFrom(request);

            const localStorageOrder: ProductionOrderDto | null = this.orderService.getOrderFromLocalStorage();

            let order: ProductionOrderDto = new ProductionOrderDto();

            if (localStorageOrder && localStorageOrder.id === orderId) {
                order = localStorageOrder;
            } else {
                order = await this.orderService.getOrderById(orderId);
            }

            const processIndex: number = order.productionOrderProcesses!.findIndex((process) => process.id === orderProcessId);

            if (processIndex < 0) {
                throw new Error(`No se ha podido encontrar el processo ${orderProcessId} en la orden ${orderId}`);
            }

            order.productionOrderProcesses![processIndex] = updatedProcess;

            this.localStorageService.setItem(ORDERS, order);

            return updatedProcess;
        } catch (error: any) {
            throw error;
        }
    }

    async setProductionProcessAsFinished(orderId: number, orderProcessId: number, userLoggedId: number, stopAction?: StopActionsEnum | null): Promise<ProductionOrderProcessDto> {
        try {
            const request = this.httpClient.post<any>(`${this.url}/setAsFinished`, {
                orderProcessId: orderProcessId,
                stopAction: stopAction,
                finishedById: userLoggedId,
            });

            const updatedProcess: ProductionOrderProcessDto = await lastValueFrom(request);

            const localStorageOrder: ProductionOrderDto | null = this.orderService.getOrderFromLocalStorage();

            let order: ProductionOrderDto = new ProductionOrderDto();

            if (localStorageOrder && localStorageOrder.id === orderId) {
                order = localStorageOrder;
            } else {
                order = await this.orderService.getOrderById(orderId);
            }

            const processIndex: number = order.productionOrderProcesses!.findIndex((process) => process.id === orderProcessId);

            if (processIndex < 0) {
                throw new Error(`No se ha podido encontrar el processo ${orderProcessId} en la orden ${orderId}`);
            }

            order.productionOrderProcesses![processIndex] = updatedProcess;

            this.localStorageService.setItem(ORDERS, order);

            return updatedProcess;
        } catch (error: any) {
            throw error;
        }
    }

    async setOrderProcessAsPaused(pause: ProductionPauseDto): Promise<ProductionOrderProcessDto> {
        try {
            const request = this.httpClient.post<ProductionOrderProcessDto>(`${this.url}/setAsPaused`, pause);

            const updatedProcess: ProductionOrderProcessDto = await lastValueFrom(request);

            return updatedProcess;
        } catch (error: any) {
            throw new Error(`No se ha podido establecer el proceso como pausado. Error: ${error.message}`);
        }
    }

    async setOrderProcessAsResumed(params: SetEndDateParams): Promise<ProductionOrderProcessDto> {
        try {
            const request = this.httpClient.patch<ProductionOrderProcessDto>(`${this.url}/resumePaused`, params);

            const updatedProcess: ProductionOrderProcessDto = await lastValueFrom(request);

            return updatedProcess;
        } catch (error: any) {
            throw new Error(`No se ha podido establecer el proceso como reanudado. Error: ${error.message}`);
        }
    }

    async getProductionTimeByOrderProcessId(orderProcessId: number): Promise<ProductionTimeDto> {
        try {
            const request = this.httpClient.get<ProductionTimeDto>(`${this.url}/getProductionTime/${orderProcessId}`);

            const response = await lastValueFrom(request);

            return response;
        } catch (error: any) {
            throw new Error(`No se ha podido obtener el tiempo de producción para el proceso. Error: ${error.message}`);
        }
    }

    async getTempLoadedRawMaterialsByMachine(machineId: number): Promise<any[]> {
        try {
            if (!machineId || machineId <= 0) {
                return [];
            }

            const request = this.httpClient.get<any[]>(`${this.url}/tempLoadedRawMaterials/machine/${machineId}`);

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se han podido obtener las materias primas temporales de la máquina ${machineId}. Error: ${error.message}`);
        }
    }

    async applyLoadedRawMaterialsToProcess(orderProcessId: number, decisions: Array<{ rawMaterialUsedId: number; preload: boolean }>, userId: number): Promise<ProductionOrderProcessDto> {
        try {
            if (!orderProcessId || orderProcessId <= 0) {
                throw new Error('El id del proceso no es válido');
            }

            const request = this.httpClient.post<ProductionOrderProcessDto>(`${this.url}/applyTempLoadedRawMaterials/${orderProcessId}`, {
                decisions,
                userId
            });

            const updatedProcess: ProductionOrderProcessDto = await lastValueFrom(request);

            return updatedProcess;
        } catch (error: any) {
            throw new Error(`No se han podido aplicar las materias primas temporales al proceso ${orderProcessId}. Error: ${error.message}`);
        }
    }

    // --- Fin nuevos métodos ---

    async getTimerByProductionOrderProcessId(productionOrderProcessId: number): Promise<number> {
        try {
            const request = this.httpClient.get<number>(`${this.url}/getElapsedTime/${productionOrderProcessId}`);

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se ha podido obtener el timer del proceso. Error: ${error.message}`);
        }
    }

    async getMachineMeasurementValues(productionProcessId: number, machineId: number, articlesIds: number[]): Promise<MachineMeasurementsValueDto[]> {
        try {
            const request = this.httpClient.get<MachineMeasurementsValueDto[]>(`${this.url}/getMachineMeasurementValues`, {
                params: {
                    productionProcessId: productionProcessId, machineId: machineId, articlesIds: articlesIds.join(',')
                }
            });

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se ha podido los valores de máquina del proceso productivo. Error: ${error.message}`);
        }
    }

    async setArticleBatches(orderProcessId: number, articleBatches: ArticleBatchDto[]): Promise<ProductionOrderProcessDto> {
        try {
            if (orderProcessId <= 0 || !articleBatches || articleBatches.length === 0) {
                throw new Error('Id del proceso o ids de los lotes están vacíos o no son válidos');
            }

            const request = this.httpClient.patch<ProductionOrderProcessDto>(`${this.url}/setArticleBatches/${orderProcessId}`, { articleBatches: articleBatches });

            const updatedProcess: ProductionOrderProcessDto = await lastValueFrom(request);

            return updatedProcess;
        } catch (error: any) {
            throw new Error(`No se ha podido asignar el lote al proceso. Error: ${error.message}`);
        }
    }

    async updateRawMaterialsUsed(producitonOrderProcessId: number, rawMaterials: RawMaterialDto[]): Promise<ProductionOrderProcessDto> {
        try {
            if (producitonOrderProcessId <= 0) {
                throw new Error('El id del proceso ha de ser mayor de 0');
            }

            if (rawMaterials === null || rawMaterials === undefined || rawMaterials.length === 0) {
                throw new Error('Las materias primas no pueden estar vacías');
            }

            const updatedProductionOrderProcess: ProductionOrderProcessDto = await lastValueFrom(this.httpClient.patch<ProductionOrderProcessDto>(`${this.url}/updateRawMaterialsUsed/${producitonOrderProcessId}`, rawMaterials));

            return updatedProductionOrderProcess;
        } catch (error: any) {
            throw new Error(`No se han podido actualizar las materias primas del proceso de orden de producción. Error: ${error.message}`);
        }
    }

    async loadRawMaterialsOnMachine(producitonOrderProcessId: number, rawMaterials: RawMaterialDto[]): Promise<ProductionOrderProcessDto> {
        try {
            if (producitonOrderProcessId <= 0) {
                throw new Error('El id del proceso ha de ser mayor de 0');
            }

            if (rawMaterials === null || rawMaterials === undefined || rawMaterials.length === 0) {
                throw new Error('Las materias primas no pueden estar vacías');
            }

            const updatedProductionOrderProcess: ProductionOrderProcessDto = await lastValueFrom(this.httpClient.patch<ProductionOrderProcessDto>(`${this.url}/loadRawMaterialsOnMachine/${producitonOrderProcessId}`, rawMaterials));

            return updatedProductionOrderProcess;
        } catch (error: any) {
            throw new Error(`No se han podido cargar las materias primas del proceso de orden de producción en la máquina. Error: ${error.message}`);
        }
    }

    async updateRawMaterialsToUse(producitonOrderProcessId: number, rawMaterialsToUse: RawMaterialToUseDto[]): Promise<ProductionOrderProcessDto> {
        try {
            if (producitonOrderProcessId <= 0) {
                throw new Error('El id del proceso ha de ser mayor de 0');
            }

            const cleanedRawMaterials = rawMaterialsToUse.map(rm => ({
                id: rm.id,
                articleId: rm.articleId,
                finalProductId: rm.finalProductId,
                description: rm.description,
                estimatedQuantity: rm.estimatedQuantity,
                unitQuantity: rm.unitQuantity,
                createdById: rm.createdById,
                updatedById: rm.updatedById
            }));

            const updatedProductionOrderProcess: ProductionOrderProcessDto = await lastValueFrom(this.httpClient.patch<ProductionOrderProcessDto>(`${this.url}/updateRawMaterialsToUse/${producitonOrderProcessId}`, cleanedRawMaterials));

            return updatedProductionOrderProcess;
        } catch (error: any) {
            throw new Error(`No se han podido actualizar las materias primas del proceso de orden de producción. Error: ${error.message}`);
        }
    }

    async deleteUsedRawMaterialById(rawMaterialId: number): Promise<void> {
        try {
            if (rawMaterialId <= 0) {
                throw new Error('El id del proceso ha de ser mayor de 0');
            }

            await lastValueFrom(this.httpClient.delete(`${this.url}/deleteUsedRawMaterialById/${rawMaterialId}`));
        } catch (error: any) {
            throw new Error(`No se ha podido eliminar la materia prima del proceso de orden de producción. Error: ${error.message}`);
        }
    }

    async updateQualityControlCounter(counter: number, orderProcessId: number): Promise<ProductionOrderProcessDto> {
        try {
            if (orderProcessId == null || orderProcessId <= 0) {
                throw new Error('Order process id can not be null or negative');
            }

            return await lastValueFrom(this.httpClient.patch<ProductionOrderProcessDto>(`${this.url}/updateQualityControlCounter/${orderProcessId}`, { qualityControlCounter: counter }));
        } catch (e: any) {
            throw new Error(`No se han podido obtener las prioridades. Error: ${e.message}`);
        }
    }

    async preStartProcess(orderProcessId: number): Promise<void> {
        try {
            const request = this.httpClient.post<ProductionOrderProcessDto>(`${this.url}/preStartProcess`, {
                orderProcessId: orderProcessId
            });

            await lastValueFrom(request);
        } catch (error: any) {
            throw error;
        }
    }

    async swapUserInProcess(machineId: number, productionOrderProcessId: number, lastPackageId?: number, previousOrderProcessId?: number): Promise<boolean> {
        try {
            return await lastValueFrom(this.httpClient.patch<boolean>(`${this.url}/swapUserInProcess`, { 
                machineId, 
                productionOrderProcessId, 
                lastPackageId,
                previousOrderProcessId
            }));
        } catch (error: any) {
            throw new Error(`No se ha podido cambiar el usuario en el proceso de la máquina. Error: ${error.message}`);
        }
    }

    async swapUserWithPreviousProcess(machineId: number, productionOrderProcessId: number, lastPackageId: number, previousOrderProcessId: number): Promise<boolean> {
        try {
            return await lastValueFrom(this.httpClient.patch<boolean>(`${this.url}/swapUserWithPreviousProcess`, { 
                machineId, 
                productionOrderProcessId, 
                lastPackageId,
                previousOrderProcessId
            }));
        } catch (error: any) {
            throw new Error(`No se ha podido cambiar el usuario con cierre de sesión anterior. Error: ${error.message}`);
        }
    }

    async endShiftSession(orderProcessId: number, userId: number, finalPackagingId?: number): Promise<void> {
            const request = this.httpClient.post<{response: boolean}>(`${this.url}/endShiftSession`, {
                orderProcessId,
                finalPackagingId
            });
            await lastValueFrom(request);
    }

    async getProductionOrderProcessFeatures(productionOrderProcessId: number): Promise<any> {
        try {

            const request = this.httpClient.get<any>(`${this.url}/productionOrderProcessFeatures/${productionOrderProcessId}`);
            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se han podido obtener las características del proceso de orden de producción. Error: ${error.message}`);
        }
    }
}
